import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { Package, TrendingUp, AlertTriangle, DollarSign, Plus, Edit, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingInventory, setEditingInventory] = useState(null);
  const [products, setProducts] = useState([]);
  const [newInventory, setNewInventory] = useState({
    product_id: '',
    size: 'M',
    color: 'Black',
    quantity: 0
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/account');
      return;
    }
    fetchData();
  }, [user, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, inventoryRes, lowStockRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/inventory`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/inventory/low-stock`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`)
      ]);
      
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setInventory(inventoryRes.data.inventory);
      setLowStock(lowStockRes.data.low_stock);
      setProducts(productsRes.data.products);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleUpdateInventory = async (item) => {
    try {
      await axios.post(
        `${API}/admin/inventory`,
        {
          product_id: item.product_id,
          size: item.size,
          color: item.color,
          quantity: item.quantity
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inventory updated');
      setEditingInventory(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update inventory:', error);
      toast.error('Failed to update inventory');
    }
  };

  const handleAddInventory = async () => {
    if (!newInventory.product_id || newInventory.quantity <= 0) {
      toast.error('Please fill all fields with valid values');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/inventory`,
        newInventory,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inventory added');
      setShowAddForm(false);
      setNewInventory({ product_id: '', size: 'M', color: 'Black', quantity: 0 });
      fetchData();
    } catch (error) {
      console.error('Failed to add inventory:', error);
      toast.error('Failed to add inventory');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'shipped': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'processing': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : productId;
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      <div className="relative z-10 pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-8" data-testid="admin-title">Admin Dashboard</h1>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 overflow-x-auto" data-testid="admin-tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'overview'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid="tab-overview"
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'orders'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid="tab-orders"
            >
              Orders
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'inventory'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid="tab-inventory"
            >
              Inventory
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && stats && (
                <div data-testid="overview-tab">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="glass-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Total Orders</span>
                        <Package className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="text-3xl font-bold" data-testid="stat-total">{stats.orders.total}</div>
                    </div>
                    <div className="glass-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Processing</span>
                        <TrendingUp className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div className="text-3xl font-bold" data-testid="stat-processing">{stats.orders.processing}</div>
                    </div>
                    <div className="glass-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Low Stock Items</span>
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      </div>
                      <div className="text-3xl font-bold text-red-400" data-testid="stat-low-stock">{stats.inventory.low_stock_items}</div>
                    </div>
                    <div className="glass-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Total Revenue</span>
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="text-3xl font-bold text-green-400" data-testid="stat-revenue">₹{stats.revenue.total.toFixed(0)}</div>
                    </div>
                  </div>

                  {/* Low Stock Alert */}
                  {lowStock.length > 0 && (
                    <div className="glass-card bg-red-500/10 border-red-500/30 mb-8" data-testid="low-stock-alert">
                      <div className="flex items-center gap-3 mb-4">
                        <AlertTriangle className="w-6 h-6 text-red-400" />
                        <h3 className="text-xl font-bold text-red-400">Low Stock Alert</h3>
                      </div>
                      <div className="space-y-2">
                        {lowStock.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <span>{getProductName(item.product_id)} - {item.size} - {item.color}</span>
                            <span className="font-bold text-red-400">{item.quantity} left</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Orders */}
                  <div className="glass-card">
                    <h3 className="text-2xl font-bold mb-4">Recent Orders</h3>
                    <div className="space-y-3">
                      {orders.slice(0, 5).map((order) => (
                        <div key={order.id} className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                          <div>
                            <div className="font-semibold">{order.user_name}</div>
                            <div className="text-sm text-gray-400">{order.items.length} items</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{order.currency === 'INR' ? '₹' : '$'}{order.total_amount}</div>
                            <span className={`text-xs px-2 py-1 rounded border capitalize ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div className="glass-card" data-testid="orders-tab">
                  <h2 className="text-2xl font-bold mb-6">All Orders</h2>
                  {orders.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">No orders yet</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left py-3 px-4">Order ID</th>
                            <th className="text-left py-3 px-4">Customer</th>
                            <th className="text-left py-3 px-4">Items</th>
                            <th className="text-left py-3 px-4">Amount</th>
                            <th className="text-left py-3 px-4">Payment</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => (
                            <tr key={order.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                              <td className="py-3 px-4">
                                <div className="font-mono text-xs">{order.id.slice(0, 8)}</div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="font-medium">{order.user_name}</div>
                                <div className="text-xs text-gray-400">{order.user_email}</div>
                              </td>
                              <td className="py-3 px-4">{order.items.length} item(s)</td>
                              <td className="py-3 px-4">
                                <div className="font-semibold">
                                  {order.currency === 'INR' ? '₹' : '$'}{order.total_amount.toFixed(2)}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`text-xs px-2 py-1 rounded capitalize ${
                                  order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {order.payment_status}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`text-xs px-2 py-1 rounded border capitalize ${getStatusColor(order.status)}`}>
                                  {order.status}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <select
                                  value={order.status}
                                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                  className="text-sm bg-gray-800 border border-gray-700 rounded px-2 py-1"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="processing">Processing</option>
                                  <option value="shipped">Shipped</option>
                                  <option value="delivered">Delivered</option>
                                  <option value="cancelled">Cancelled</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Inventory Tab */}
              {activeTab === 'inventory' && (
                <div data-testid="inventory-tab">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Inventory Management</h2>
                    <button
                      onClick={() => setShowAddForm(!showAddForm)}
                      className="btn-primary flex items-center gap-2"
                      data-testid="add-inventory-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Inventory
                    </button>
                  </div>

                  {/* Add Inventory Form */}
                  {showAddForm && (
                    <div className="glass-card mb-6" data-testid="add-inventory-form">
                      <h3 className="text-xl font-bold mb-4">Add New Inventory</h3>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <select
                          value={newInventory.product_id}
                          onChange={(e) => setNewInventory({...newInventory, product_id: e.target.value})}
                          className="bg-gray-800 border border-gray-700 rounded px-3 py-2"
                        >
                          <option value="">Select Product</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <select
                          value={newInventory.size}
                          onChange={(e) => setNewInventory({...newInventory, size: e.target.value})}
                          className="bg-gray-800 border border-gray-700 rounded px-3 py-2"
                        >
                          <option value="S">S</option>
                          <option value="M">M</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                        </select>
                        <select
                          value={newInventory.color}
                          onChange={(e) => setNewInventory({...newInventory, color: e.target.value})}
                          className="bg-gray-800 border border-gray-700 rounded px-3 py-2"
                        >
                          <option value="Black">Black</option>
                          <option value="White">White</option>
                          <option value="Navy">Navy</option>
                          <option value="Grey">Grey</option>
                          <option value="Red">Red</option>
                          <option value="Green">Green</option>
                          <option value="Blue">Blue</option>
                        </select>
                        <input
                          type="number"
                          value={newInventory.quantity}
                          onChange={(e) => setNewInventory({...newInventory, quantity: parseInt(e.target.value) || 0})}
                          placeholder="Quantity"
                          className="bg-gray-800 border border-gray-700 rounded px-3 py-2"
                          min="0"
                        />
                        <div className="flex gap-2">
                          <button onClick={handleAddInventory} className="btn-primary flex-1">
                            <Save className="w-4 h-4" />
                          </button>
                          <button onClick={() => setShowAddForm(false)} className="btn-secondary">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Inventory Table */}
                  <div className="glass-card">
                    {inventory.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">No inventory data. Add some!</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-700">
                              <th className="text-left py-3 px-4">Product</th>
                              <th className="text-left py-3 px-4">Size</th>
                              <th className="text-left py-3 px-4">Color</th>
                              <th className="text-left py-3 px-4">Quantity</th>
                              <th className="text-left py-3 px-4">Status</th>
                              <th className="text-left py-3 px-4">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inventory.map((item) => (
                              <tr key={item.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                                <td className="py-3 px-4">
                                  <div className="font-medium">{getProductName(item.product_id)}</div>
                                </td>
                                <td className="py-3 px-4">{item.size}</td>
                                <td className="py-3 px-4">{item.color}</td>
                                <td className="py-3 px-4">
                                  {editingInventory?.id === item.id ? (
                                    <input
                                      type="number"
                                      value={editingInventory.quantity}
                                      onChange={(e) => setEditingInventory({...editingInventory, quantity: parseInt(e.target.value) || 0})}
                                      className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                                      min="0"
                                    />
                                  ) : (
                                    <span className="font-semibold">{item.quantity}</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {item.quantity <= item.low_stock_threshold ? (
                                    <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">Low Stock</span>
                                  ) : (
                                    <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">In Stock</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {editingInventory?.id === item.id ? (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleUpdateInventory(editingInventory)}
                                        className="text-green-400 hover:text-green-300"
                                      >
                                        <Save className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setEditingInventory(null)}
                                        className="text-red-400 hover:text-red-300"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setEditingInventory(item)}
                                      className="text-purple-400 hover:text-purple-300"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
